import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import authService from '../services/auth';
import AuthPageWrapper from '../components/layout/AuthPageWrapper';
import './Login.css';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [userId, setUserId] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [sessionReplacedMessage, setSessionReplacedMessage] = useState('');

  useEffect(() => {
    if (searchParams.get('session_replaced') === '1') {
      setSessionReplacedMessage('You were signed in on another device. Please sign in again.');
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const validateEmail = (email) => {
    // Basic email format validation
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      // Clear previous state
      setShowTwoFactor(false);
      setUserId(null);
      setTwoFactorCode('');

      // Validate email format
      if (!email.trim()) {
        throw new Error('Please enter your email address');
      }
      if (!validateEmail(email)) {
        throw new Error('Please enter a valid email address');
      }

      // Validate password
      if (!password.trim()) {
        throw new Error('Please enter your password');
      }

      const response = await authService.login(email, password);

      if (response.token) {
        // Direct login successful - store token and redirect
        if (response.message && response.message.includes('2FA session valid')) {
          setSuccessMessage('Login successful! (2FA session valid) Redirecting...');
        } else {
          setSuccessMessage('Login successful! Redirecting...');
        }
        setTimeout(() => navigate('/app'), 1000);
      } else if (response.userId) {
        // 2FA required - fallback for old flow
        setUserId(response.userId);
        setShowTwoFactor(true);
        setSuccessMessage('2FA code has been sent to your email');
      }
    } catch (err) {
      setError(err.message);
      // Clear password on error for security
      setPassword('');
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFactorVerification = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      if (!twoFactorCode.trim()) {
        throw new Error('Please enter the 2FA code from your email');
      }

      const response = await authService.verify2FA(userId, twoFactorCode);
      if (response.token) {
        setSuccessMessage('Login successful! Redirecting...');
        setTimeout(() => navigate('/app'), 1000);
      }
    } catch (err) {
      setError(err.message);
      setTwoFactorCode('');
    } finally {
      setLoading(false);
    }
  };


  return (
     <AuthPageWrapper>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-xl-7 col-lg-8 col-md-10">
            <div className="login-card wow fadeInUp" data-wow-delay=".2s">
              {/* Header */}
              <div className="text-center mb-4">
                <h2 className="login-title">Welcome Back</h2>
                <p className="login-subtitle">
                  {showTwoFactor ? 'Enter your authentication code' : 'Sign in to your account'}
                </p>
              </div>

              {/* Alerts */}
              {sessionReplacedMessage && (
                <div className="alert alert-info alert-dismissible fade show" role="alert">
                  <i className="lni lni-information"></i> {sessionReplacedMessage}
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setSessionReplacedMessage('')}
                    aria-label="Close"
                  ></button>
                </div>
              )}

              {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  <i className="lni lni-cross-circle"></i> {error}
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setError('')}
                    aria-label="Close"
                  ></button>
                </div>
              )}

              {successMessage && (
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                  <i className="lni lni-checkmark-circle"></i> {successMessage}
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setSuccessMessage('')}
                    aria-label="Close"
                  ></button>
                </div>
              )}

              {/* Login Form */}
              <form onSubmit={showTwoFactor ? handleTwoFactorVerification : handleLogin}>
                {!showTwoFactor ? (
                  <>
                    <div className="mb-3">
                      <input
                        type="email"
                        className={`form-control ${email && !validateEmail(email) ? 'is-invalid' : ''}`}
                        id="email"
                        name="email"
                        placeholder="Email Address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        autoFocus
                      />
                      {email && !validateEmail(email) && (
                        <div className="invalid-feedback">
                          Please enter a valid email address
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <input
                        type="password"
                        className="form-control"
                        id="password"
                        name="password"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                    </div>

                    <button 
                      type="submit" 
                      className="ud-main-btn w-100 mb-3"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Signing in...
                        </>
                      ) : (
                        'Sign In'
                      )}
                    </button>

                    <div className="text-center mb-3">
                      <Link to="/forgot-password" className="text-decoration-none">
                        Forgot your password?
                      </Link>
                    </div>

                    <div className="login-divider">
                      <span>or</span>
                    </div>

                    <Link to="/register" className="ud-main-btn ud-white-btn w-100">
                      Create New Account
                    </Link>
                  </>
                ) : (
                  <>
                    <div className="mb-3">
                      <input
                        type="text"
                        className="form-control text-center"
                        id="twoFactorCode"
                        name="twoFactorCode"
                        placeholder="Enter 6-digit verification code"
                        value={twoFactorCode}
                        onChange={(e) => setTwoFactorCode(e.target.value)}
                        required
                        autoFocus
                        maxLength="6"
                      />
                      <small className="form-text text-muted">
                        Check your email for the verification code
                      </small>
                    </div>

                    <button 
                      type="submit" 
                      className="ud-main-btn w-100"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Verifying...
                        </>
                      ) : (
                        'Verify Code'
                      )}
                    </button>
                  </>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
      </AuthPageWrapper>
  );
};

export default Login;
